import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import TextEditor from "../../../text-editor/TextEditor";
import { SortableList } from "../../../SortableList";
import {
  Button,
  Col,
  Flex,
  InputNumber,
  message,
  Modal,
  Radio,
  RadioChangeEvent,
  Row,
  Typography,
} from "antd";
import {
  CheckOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useAddLesson } from "../../../../stores/stores";
import {
  initialCalculatedQuestionValue,
  ModeQuestion,
  QuestionTypeEnum,
} from "../../../../constants";
import {
  QuestionAnswerInfo,
  QuestionData,
  QuestionItemDetail,
} from "../../../../types/course-construction";
import { findMaxIndexOfArray, mapAnsBEtoFE } from "../../../../utils/arrays";
import {
  renderQuestionNameByType,
  renderQuestionDescriptionByType,
} from "../../../../utils/functionUltils";
import QuestionShortTextItem from "./QuestionShortTextItem";
import {
  getRandomUniqueNumbers,
  updateValueAtIndex,
} from "../../../../utils/app";
import "./style.scss";
import { extractVariablesInBrackets } from "../calculated/utils";
import { VariableDefinitionsProps } from "../../../../types/course";
import { useDirty } from "../../../../contexts/DirtyProvider";

function CalculatedMulti({
  data = {} as QuestionData,
  defaultName,
}: QuestionItemDetail) {
  const { t } = useTranslation();
  const question = useAddLesson((state: any) => state.question);
  const [questionName, setQuestionName] = useState<string>("");
  const [answers, setAnswers] = useState(initialCalculatedQuestionValue);
  const modeQuestion = useAddLesson((state) => state.modeQuestion);
  const questType = data.type;

  const [variableDefinitions, setVariableDefinitions] = useState<
    VariableDefinitionsProps[]
  >([]);
  const [tolerance, setTolerance] = useState<number>(0); // Sai số

  const [randomEditValue, setRandomEditValue] = useState<number | null>(null);
  const [randomNewValue, setRandomNewValue] = useState<number>(0);
  const [radioValue, setRadioValue] = useState<number>(0);

  const { setDirty } = useDirty();

  const setCurrentQuestionInfo = useAddLesson(
    (state) => state.setCurrentQuestionInfo
  );

  const handleChangeText = (current: { id: number; value: string }) => {
    const distAnswer = [...answers];
    const indexOfChangeItem = distAnswer.findIndex(
      (item: QuestionAnswerInfo) => item.id === current.id
    );
    if (indexOfChangeItem !== -1) {
      distAnswer[indexOfChangeItem] = {
        ...distAnswer[indexOfChangeItem],
        value: current.value,
      };
      setAnswers(distAnswer);
    }
  };

  const handleRemoveOneChoose = (id: number) => {
    if (id) {
      const newList = [...answers];
      setAnswers(newList.filter((item: QuestionAnswerInfo) => item.id !== id));
    } else console.error("Cannot find id");
  };

  const handleAddMoreChoose = () => {
    if (answers.length > 0) {
      const newItem = {
        id: findMaxIndexOfArray(answers) + 1,
        value: "",
        key: findMaxIndexOfArray(answers) + 1,
        isTrue: false,
        point: 0,
        variableDefinitions: [],
        tolerance: 0,
      };
      const newList = [...answers];
      setAnswers(newList.concat([newItem]));
    } else setAnswers(initialCalculatedQuestionValue);
    setDirty(true);
  };

  useEffect(() => {
    resetConfig();
  }, []);

  useEffect(() => {
    const vars = extractVariablesInBrackets(questionName as string);

    if (variableDefinitions.length) {
      //---------Xoá biến ở câu hỏi----------
      const newVars: VariableDefinitionsProps[] = [];
      for (let i = 0; i < variableDefinitions.length; i++) {
        if (!vars.includes(variableDefinitions[i].key)) {
          setVariableDefinitions((prevValue: VariableDefinitionsProps[]) =>
            prevValue.filter(
              (stateVa: VariableDefinitionsProps) =>
                stateVa.key !== variableDefinitions[i].key
            )
          );
        }
      }
      //-------------------------------------

      //-----------------Thêm biến trong câu hỏi đã có ít nhất 1 biến trước đó-----------------
      vars.forEach((v: string) => {
        if (
          !variableDefinitions.some(
            (va: VariableDefinitionsProps) => va.key === v
          )
        ) {
          newVars.push({
            key: v,
            maxValue: 0,
            minValue: 0,
            predefinedValues: [],
            countRandom: 0,
            isShowEditRandom: false,
          });
        }
      });
      setVariableDefinitions((prevValue: VariableDefinitionsProps[]) => [
        ...prevValue,
        ...newVars,
      ]);
      // ---------------------------------------------------------------------------
    }
    //Chưa có biến
    else
      setVariableDefinitions(
        vars.map((v: string) => ({
          key: v,
          maxValue: 0,
          minValue: 0,
          predefinedValues: [],
          countRandom: 0,
          isShowEditRandom: false,
        }))
      );
  }, [questionName]);

  useEffect(() => {
    setCurrentQuestionInfo({
      ...answers,
      questionName: questionName,
      variableDefinitions: variableDefinitions,
      tolerance: tolerance,
    });
  }, [answers, questionName, variableDefinitions, tolerance]);

  useEffect(() => {
    setQuestionName(question.question);
    if (modeQuestion === ModeQuestion.UPDATE && data.answers) {
      const feAnswer = mapAnsBEtoFE(data.answers);
      setAnswers(feAnswer as any);
      setTolerance(feAnswer[0]?.tolerance as number);
      const variableDefinitionsBe = feAnswer[0]?.variableDefinitions;
      if (variableDefinitionsBe?.length)
        setVariableDefinitions(
          variableDefinitionsBe.map((v: VariableDefinitionsProps) => ({
            ...v,
            countRandom: v.predefinedValues?.length,
            isShowEditRandom: false,
          }))
        );
      const correctAns = feAnswer.find((quiz: any) => quiz.isTrue);
      if (correctAns) {
        setRadioValue(correctAns.id);
      }
    } else {
      setAnswers([]);
    }
  }, [question, data.answers, modeQuestion]);

  const resetConfig = () => {
    setVariableDefinitions([]);
    setRandomEditValue(null);
    setRandomNewValue(0);
    setTolerance(0);
    setAnswers([]);
    setQuestionName("");
  };

  const handleChangeQuestionName = (qName: string) => {
    setQuestionName(qName);
  };

  const handleDragChange = (value: any) => {
    setAnswers(value);
    setDirty(true);
  };

  const handleEditRandomValue = (index: number, key: string) => {
    if (randomNewValue) {
      try {
        const current = variableDefinitions.find(
          (v: VariableDefinitionsProps) => v.key === key
        );

        if (!current) return;

        const newRandomValues = updateValueAtIndex(
          current.predefinedValues,
          index,
          randomNewValue
        );

        const newVariable = variableDefinitions.map(
          (v: VariableDefinitionsProps) => {
            if (v.key === key)
              return { ...v, predefinedValues: newRandomValues };
            return v;
          }
        );
        setVariableDefinitions(newVariable);
        setRandomEditValue(null);
      } catch (err: any) {
        message.warning(err.message);
      }
    }
  };

  const handleAddPrevValue = (key: string) => {
    const current = variableDefinitions.find(
      (v: VariableDefinitionsProps) => v.key === key
    );

    if (!current) return;

    if (!current.maxValue) {
      Modal.confirm({
        title: `Cảnh báo`,
        content: `Vui lòng nhập giá trị MIN, MAX`,
        cancelText: t("Huỷ", "Huỷ"),
        centered: true,
        icon: <ExclamationCircleOutlined style={{ color: "#1677FF" }} />,
        footer: (_, { OkBtn, CancelBtn }) => (
          <>
            <OkBtn />
          </>
        ),
      });
    } else {
      try {
        const prevValues = getRandomUniqueNumbers(
          current.minValue,
          current.maxValue,
          current.countRandom
        );
        const newVariable = variableDefinitions.map(
          (v: VariableDefinitionsProps) => {
            if (v.key === key)
              return {
                ...v,
                predefinedValues: prevValues,
                isShowEditRandom: false,
              };
            return v;
          }
        );
        setVariableDefinitions(newVariable);
      } catch (err: any) {
        message.warning(err.message);
      }
    }
  };

  const handleClickPlus = (key: string) => {
    const newVariable = variableDefinitions.map(
      (v: VariableDefinitionsProps) => {
        if (v.key === key) return { ...v, isShowEditRandom: true };
        return v;
      }
    );
    setVariableDefinitions(newVariable);
  };

  const handleRefreshPreValue = (key: string) => {
    Modal.confirm({
      title: `Xác nhận làm mới`,
      content: `Bạn có chắc muốn xoá các giá trị ngẫu nhiên hiện tại?`,
      onOk: () => {
        const newVariable = variableDefinitions.map(
          (v: VariableDefinitionsProps) => {
            if (v.key === key)
              return { ...v, predefinedValues: [], countRandom: 0 };
            return v;
          }
        );
        setVariableDefinitions(newVariable);
        setDirty(true);
      },
      okText: t("Xác nhận", "Xác nhận"),
      cancelText: t("Huỷ", "Huỷ"),
      centered: true,
      icon: <ExclamationCircleOutlined style={{ color: "#1677FF" }} />,
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <OkBtn />
        </>
      ),
    });
  };

  const handleChangeMax = (key: string, value: number) => {
    const newVariable = variableDefinitions.map(
      (v: VariableDefinitionsProps) => {
        if (v.key === key) return { ...v, maxValue: value };
        return v;
      }
    );
    setVariableDefinitions(newVariable);
  };

  const handleChangeMin = (key: string, value: number) => {
    const newVariable = variableDefinitions.map(
      (v: VariableDefinitionsProps) => {
        if (v.key === key) return { ...v, minValue: value };
        return v;
      }
    );
    setVariableDefinitions(newVariable);
  };

  const handleChangeCountRandom = (key: string, value: number) => {
    const newVariable = variableDefinitions.map(
      (v: VariableDefinitionsProps) => {
        if (v.key === key) return { ...v, countRandom: value };
        return v;
      }
    );
    setVariableDefinitions(newVariable);
  };

  const handleChangeRadio = (e: RadioChangeEvent) => {
    const currentActiveRadioId = e.target.value;
    setRadioValue(currentActiveRadioId);
    const newAns = answers.map((item) =>
      item.id === currentActiveRadioId ? { ...item, isTrue: true } : item
    );
    setAnswers(newAns);
  };

  return (
    <section className="question-wrapper calculated">
      <Typography.Title level={5} className="question-title">
        {renderQuestionNameByType(question.type)}
      </Typography.Title>
      <Typography.Title
        level={1}
        className="question-description secondary-color"
      >
        {renderQuestionDescriptionByType(question.type)}
      </Typography.Title>
      <TextEditor
        value={questionName || defaultName}
        onChange={handleChangeQuestionName}
      />
      <div className="detail-calculated-config">
        <Row gutter={[12, 12]}>
          {variableDefinitions?.length > 0 &&
            variableDefinitions.map((variable: VariableDefinitionsProps) => (
              <Col key={variable.key} md={24} xl={12}>
                <div className="font-weight-5">
                  {t("Thiết lập các giá trị cho:", "Thiết lập các giá trị cho:")}{" "}
                  <span className="text-primary">{variable.key}</span>
                </div>
                <Flex align="center" gap={12} className="mt-1">
                  <span>
                    <span className="text-danger">*</span> {t("Giá trị MAX:", "Giá trị MAX:")}{" "}
                  </span>
                  <InputNumber
                    value={variable.maxValue}
                    onChange={(value) => {
                      handleChangeMax(variable.key, value as number);
                      setDirty(true);
                    }}
                  />
                </Flex>

                <Flex align="center" gap={12} className="mt-1">
                  <span>
                    <span className="text-danger">*</span> {t("Giá trị MIN:", "Giá trị MIN:")}{" "}
                  </span>
                  <InputNumber
                    value={variable.minValue}
                    onChange={(value) => {
                      handleChangeMin(variable.key, value as number);
                      setDirty(true);
                    }}
                  />
                </Flex>

                <Flex className="mt-1" align="center" gap={4} wrap>
                  <div>
                    <span>
                      <span className="text-danger">* </span>Số giá trị ngẫu
                      nhiên:
                    </span>
                  </div>{" "}
                  {variable.isShowEditRandom ? (
                    <Flex align="center" gap={8}>
                      <InputNumber
                        value={variable.countRandom}
                        onChange={(value) => {
                          handleChangeCountRandom(variable.key, value as number);
                          setDirty(true);
                        }}
                      />{" "}
                      <Button
                        onClick={() => {handleAddPrevValue(variable.key); setDirty(true);}}
                        style={{ width: 20, height: 20 }}
                        className="btn-primary ml-2"
                        icon={<CheckOutlined style={{ fontSize: 10 }} />}
                      ></Button>
                    </Flex>
                  ) : variable.countRandom > 0 ? (
                    variable.countRandom
                  ) : (
                    <Button
                      onClick={() => {handleClickPlus(variable.key); setDirty(true);}}
                      style={{ width: 20, height: 20 }}
                      className="btn-primary ml-2"
                      icon={<PlusOutlined style={{ fontSize: 10 }} />}
                    ></Button>
                  )}
                  {variable.predefinedValues?.length > 0 && (
                    <Button
                      style={{ width: 25, height: 25, marginLeft: 12 }}
                      icon={<UndoOutlined style={{ fontSize: 15 }} />}
                      onClick={() => handleRefreshPreValue(variable.key)}
                    ></Button>
                  )}
                </Flex>
                <Flex
                  className="mt-1 mb-1"
                  align="center"
                  justify="center"
                  gap={12}
                  wrap
                >
                  {variable.predefinedValues?.map(
                    (preVal: number, index: number) => (
                      <Flex align="center">
                        <div
                          className="font-weight-6 ml-1 mr-1 pointer random-value"
                          onClick={() => {
                            setRandomEditValue(preVal);
                            setRandomNewValue(preVal);
                            setDirty(true);
                          }}
                        >
                          {preVal}
                        </div>
                        {randomEditValue === preVal &&
                          questType !== QuestionTypeEnum.CALCULATED_SIMPLE && (
                            <Flex align="center" gap={8}>
                              <InputNumber
                                style={{ width: 80 }}
                                value={randomNewValue}
                                onChange={(value) => {
                                  setRandomNewValue(value as number);
                                  setDirty(true);
                                }}
                              />
                              <CheckOutlined
                                className="pointer"
                                onClick={() => {
                                  handleEditRandomValue(index, variable.key);
                                  setDirty(true);
                                }}
                              />
                            </Flex>
                          )}
                      </Flex>
                    )
                  )}
                </Flex>
              </Col>
            ))}
        </Row>
        <Flex align="center" gap={12} className="mt-1">
          {t("Sai số cho phép:", "Sai số cho phép:")}{" "}
          <InputNumber
            value={tolerance}
            onChange={(value) => {setTolerance(value as number); setDirty(true);}}
          />
        </Flex>
      </div>

      <div className="tree-course-construct list-answers choose-text mt-2">
        <Radio.Group
          name="question-text"
          onChange={handleChangeRadio}
          value={radioValue}
          className="w-full"
        >
          <SortableList
            items={answers}
            onChange={handleDragChange}
            renderItem={(record: any) => (
              <SortableList.Item id={record.id}>
                <SortableList.DragHandle />
                <QuestionShortTextItem
                  index={record.id}
                  value={record.value}
                  radioValue={record.id}
                  onChange={handleChangeText}
                  onDelete={handleRemoveOneChoose}
                />
              </SortableList.Item>
            )}
          />
        </Radio.Group>
        <Button
          icon={<PlusOutlined className="size-22" />}
          className="btn-add-more mt-2"
          onClick={handleAddMoreChoose}
        >
          {t("Thêm câu trả lời", "Thêm câu trả lời")}
        </Button>
      </div>
    </section>
  );
}

export default CalculatedMulti;
